import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ModalContext } from '@notino/react-styleguide';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { trackDrawerClose } from '@components/BottomSheet/tracking';
import {
  FeatureFlags,
  useFeatureFlags,
} from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { isDesktop } from '@utils/helpers';

import { PopupBox } from '../../../../../components/PopupBox';
import { useProductDetailContext } from '../../../ProductDetailContext';

import { Content } from './Content';
import discoBoxImage from './discoBoxImageSmall.webp';
import { messages } from './messages';
import { GetSamplesText, Title } from './styled';

const DiscoveryBoxSelfChoice = () => {
  const { toggleModal } = ModalContext.useModalContext();
  const { toggleDrawer } = useDrawer();
  const { product, currentVariant } = useProductDetailContext();
  const { getTimeFromInit } = useTrackingContext();

  const featureFlags = useFeatureFlags();
  const sampleProductId = getRenderableDiscoBoxSampleId(
    product,
    currentVariant,
    featureFlags as Pick<FeatureFlags, 'discovery_box_self_choice_enabled'>
  );

  if (!sampleProductId) {
    return null;
  }

  const openModalOrDrawer = () => {
    dispatchTrackingEvent({
      event: 'element_click',
      _clear: true,
      element: {
        timing: getTimeFromInit(),
        interaction: 'click',
        name: 'discovery_box_pdp_open',
        type: 'discovery_box_pdp',
        action: 'discovery_box_pdp_open',
      },
    });

    if (isDesktop()) {
      toggleModal(<Content sampleProductId={sampleProductId} />);
      return;
    }

    toggleDrawer(<Content sampleProductId={sampleProductId} />, {
      header: (
        <Title>
          <FormattedMessage {...messages.modalTitle} />
        </Title>
      ),
      onDismiss: (closeType) =>
        trackDrawerClose({
          closeType,
          timing: getTimeFromInit(),
          name: 'discovery_box_pdp_close',
          type: 'discovery_box_pdp',
          action: 'discovery_box_pdp_close',
        }),
    });
  };

  return (
    <PopupBox
      publicImageUrl={discoBoxImage}
      imageAlt="discovery box"
      onClick={openModalOrDrawer}
      dataTestId="discoveryBoxSelfChoice"
    >
      <GetSamplesText>
        <FormattedMessage
          {...messages.text}
          values={{
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      </GetSamplesText>
    </PopupBox>
  );
};

export const getRenderableDiscoBoxSampleId = (
  product: {
    features?: {
      selfChoiceBox?: { sampleProductId?: number | null } | null;
    } | null;
  },
  variant: {
    features?: {
      selfChoiceBox?: { sampleProductId?: number | null } | null;
    } | null;
  },
  featureFlags: Pick<FeatureFlags, 'discovery_box_self_choice_enabled'>
) => {
  if (!featureFlags.discovery_box_self_choice_enabled) {
    return null;
  }
  return (
    product.features?.selfChoiceBox?.sampleProductId ||
    variant.features?.selfChoiceBox?.sampleProductId
  );
};

export default DiscoveryBoxSelfChoice;
